import { usePurchase } from "../../../context/purchaseContext"
import useStrings from "../../../localization/useStrings"
import useQueryParams from "../../../hooks/useQueryParams"
import { formatCurrency, formatProductPrice } from "../../../utils/utils"
import native from "../../../native/native"
import styles from "./ExpirationPaymentButtons.module.scss"

interface Props {
  onLifetimeClick: () => void
}

const ExpirationPaymentButtons = ({ onLifetimeClick }: Props) => {
  const { state: purchaseState, actions: purchaseActions } = usePurchase()
  const strings = useStrings()
  const queryParams = useQueryParams()

  const show3Month = queryParams.show3month

  const monthlyProduct = purchaseState.selectedPlan.monthlyProduct
  const yearlyProduct = purchaseState.selectedPlan.yearlyProduct
  const threeMonthProduct = purchaseState.selectedPlan.threeMonthsProduct
  const isUltimate = purchaseState.selectedPlan.isUltimate

  const monthlyPrice = formatProductPrice(monthlyProduct)
  const yearlyPrice = formatProductPrice(yearlyProduct)
  const threeMonthPrice = threeMonthProduct
    ? formatProductPrice(threeMonthProduct)
    : null
  const lifetimeMultiplier = isUltimate ? 3.333333 : 5
  const lifetimePrice = formatCurrency(
    Math.round(yearlyProduct.price * lifetimeMultiplier + 0.01) - 0.01,
    yearlyProduct.priceLocale,
  )
  const monthlyFromYearly = formatCurrency(
    yearlyProduct.price / 12,
    yearlyProduct.priceLocale,
  )

  const handleMonthlyClick = () => {
    purchaseActions.purchaseProduct(monthlyProduct)
  }

  const handleYearlyClick = () => {
    purchaseActions.purchaseProduct(yearlyProduct)
  }

  const handleLifetimeClick = () => {
    native.postNativeEvent("analyitcs", {
      name: "html_lifetime_click",
    })
    onLifetimeClick()
  }

  const handle3MonthClick = () => {
    if (threeMonthProduct) {
      purchaseActions.purchaseProduct(threeMonthProduct)
    }
  }

  return (
    <div className={`${styles.container} ${isUltimate ? styles.ultimate : ""}`}>
      {/* Monthly Button */}
      <div className={styles.buttonWrapper}>
        <button
          className={`${styles.paymentButton} ${styles.monthlyButton}`}
          onClick={handleMonthlyClick}
        >
          <div className={styles.header}>
            <span className={styles.planType}>
              {strings.bubbleExpiration.monthly}
            </span>
          </div>
          <div className={styles.content}>
            <div className={styles.price}>{monthlyPrice}</div>
            <div className={styles.perPeriod}>
              {strings.bubbleExpiration.perMonth}
            </div>
          </div>
        </button>
      </div>

      {/* Yearly Button */}
      <div className={styles.yearlyWrapper}>
        <div className={styles.popularBadge}>
          {strings.popular.toUpperCase()}
        </div>
        <button
          className={`${styles.paymentButton} ${styles.yearlyButton}`}
          onClick={handleYearlyClick}
        >
          <div className={styles.header}>
            <span className={styles.planType}>
              {strings.bubbleExpiration.yearly}
            </span>
          </div>
          <div className={styles.content}>
            <div className={styles.price}>{yearlyPrice}</div>
            <div className={styles.perPeriod}>
              {strings.bubbleExpiration.perYear}
            </div>
          </div>
        </button>
      </div>

      {/* 3-Month or Lifetime Button */}
      {show3Month && threeMonthProduct ? (
        <div className={styles.buttonWrapper}>
          <button
            className={`${styles.paymentButton} ${styles.threeMonthButton}`}
            onClick={handle3MonthClick}
          >
            <div className={styles.header}>
              <span className={styles.planType}>
                {strings.bubbleExpiration.threeMonths}
              </span>
            </div>
            <div className={styles.content}>
              <div className={styles.price}>{threeMonthPrice}</div>
              <div className={styles.perPeriod}>&nbsp;</div>
            </div>
          </button>
        </div>
      ) : (
        <div className={styles.buttonWrapper}>
          <button
            className={`${styles.paymentButton} ${styles.lifetimeButton}`}
            onClick={handleLifetimeClick}
          >
            <div className={styles.header}>
              <span className={styles.planType}>
                {strings.bubbleExpiration.lifetime}
              </span>
            </div>
            <div className={styles.content}>
              <div className={styles.price}>{lifetimePrice}</div>
              <div className={styles.perPeriod}>
                {strings.bubbleExpiration.justOnce}
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  )
}

export default ExpirationPaymentButtons
